import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Card, CardContent, Grid, Link, Typography } from '@mui/material'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { isNil, orderBy, round } from 'lodash'
import { CardTitle } from '../../../components/card/NewCard'
import MessageCard from '../../../components/card/MessageCard'
import ProgressCard from '../../../components/card/ProgressCard'
import { NewTable } from '../../../components/datatable/NewTable'
import StationAction from '../../actions/StationAction'
import { STATION_TYPE_CONSTANT } from '../../constants/StationConstants'
import { INDICATOR_OBJECT_TYPE, INDICATOR_TYPE, MAX_SCORE, MIN_SCORE, SCORE_BANDS } from '../../../import/constants/IndicatorsConstants'
import { disabledColor } from '../../../utils/constants/ColorTheme'

const FOLLOW_UP_ROUTES = {
    [STATION_TYPE_CONSTANT.PIEZOMETRY]: { station: 'piezometry', tab: 'piezometricFollowUp' },
    [STATION_TYPE_CONSTANT.HYDROMETRY]: { station: 'hydrometry', tab: 'hydrometricFollowUp' },
}

const getFollowUpLink = ({ stationId, stationType }) => {
    const route = FOLLOW_UP_ROUTES[stationType]
    return route && `/station/${route.station}/${stationId}/${route.tab}`
}

const getBandColor = (score, returnPeriod) => SCORE_BANDS.find(band => band.code === returnPeriod)?.color
    ?? SCORE_BANDS.find(band => score >= band.from)?.color
    ?? SCORE_BANDS[SCORE_BANDS.length - 1].color

const ColoredChip = ({ label, color }) => (
    <span style={{ backgroundColor: color, color: '#000', padding: '3px 8px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
        {label}
    </span>
)

ColoredChip.propTypes = {
    label: PropTypes.string,
    color: PropTypes.string,
}

const ScoreGauge = ({ score }) => (
    <Grid container alignItems='center' spacing={1}>
        <Grid size='auto'>
            <Typography variant='h4' sx={{ fontWeight: 'bold' }}>{round(score)}</Typography>
        </Grid>
        <Grid size='auto'>
            <Typography variant='body2' color='text.secondary'>{`/${MAX_SCORE}`}</Typography>
        </Grid>
        <Grid size='grow'>
            <div style={{ position: 'relative', display: 'flex', height: '14px', borderRadius: '3px', overflow: 'hidden' }}>
                {orderBy(SCORE_BANDS, 'from').map(band => (
                    <div key={band.code} style={{ flex: band.to - band.from, backgroundColor: band.color }} />
                ))}
                <div
                    style={{
                        position: 'absolute',
                        left: `calc(${Math.min(MAX_SCORE, Math.max(MIN_SCORE, score))}% - 1px)`,
                        top: 0,
                        bottom: 0,
                        width: '3px',
                        backgroundColor: '#000',
                    }}
                />
            </div>
        </Grid>
    </Grid>
)

ScoreGauge.propTypes = {
    score: PropTypes.number.isRequired,
}

const WaterStressCard = ({
    objectType,
    objectCode,
}) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [summary, setSummary] = useState({})
    const [isLoaded, setIsLoaded] = useState(false)

    const isProductionUnit = objectType === INDICATOR_OBJECT_TYPE.PRODUCTION_UNIT

    useEffect(() => {
        if (isNil(objectCode)) {
            return
        }
        setIsLoaded(false)
        dispatch(StationAction.fetchIndicatorSummary({
            objectType,
            indicatorType: INDICATOR_TYPE.WATER_STRESS,
            objectCode,
        }))
            .then(result => setSummary(result ?? {}))
            .finally(() => setIsLoaded(true))
    }, [objectType, objectCode])

    const rows = useMemo(() => (summary.details ?? []).map(detail => {
        const label = detail.name ? `[${detail.code}] ${detail.name}` : detail.code
        const link = getFollowUpLink(detail)
        // Sans score, la source fait partie de l'objet mais n'a pas contribué au calcul ce jour-là
        // (données manquantes ou non qualifiées) : ligne grisée, score et part vides.
        const withoutScore = isNil(detail.score)
        return {
            name: {
                value: withoutScore ? (
                    <span style={{ color: disabledColor }}>{label}</span>
                ) : (!link ? label : (
                    <Link underline='hover' sx={{ cursor: 'pointer' }} onClick={() => navigate(link)}>{label}</Link>
                )),
            },
            weight: {
                value: isNil(detail.weight) ? '' : round(detail.weight * 100, 1),
                align: 'center',
            },
            score: {
                value: withoutScore ? '' : round(detail.score),
                color: withoutScore ? undefined : getBandColor(detail.score, detail.returnPeriod),
                align: 'center',
                tooltip: detail.returnPeriod ? <ColoredChip label={i18n[detail.returnPeriod]} color={getBandColor(detail.score, detail.returnPeriod)} /> : undefined,
            },
        }
    }), [summary, navigate])

    const headers = useMemo(() => [
        { key: 'name', value: isProductionUnit ? i18n.resources : i18n.stations, noSort: true },
        { key: 'weight', value: '%', noSort: true, width: 60 },
        { key: 'score', value: i18n.score, noSort: true, width: 60 },
    ], [isProductionUnit])

    return (
        <Card>
            <CardTitle title={isProductionUnit ? i18n.waterStressOfProductionUnit : i18n.waterStressOfResource} />
            <CardContent>
                {!isLoaded && (<ProgressCard indeterminate />)}
                {isLoaded && isNil(summary.score) && (<MessageCard>{i18n.noDataToDisplay}</MessageCard>)}
                {isLoaded && !isNil(summary.score) && (
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <ScoreGauge score={summary.score} />
                        </Grid>
                        {rows.length > 0 && (
                            <Grid size={12}>
                                <NewTable
                                    rows={rows}
                                    headers={headers}
                                    rowsPerPageOptions={[{ value: 1000, label: '1000' }]}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
            </CardContent>
        </Card>
    )
}

WaterStressCard.propTypes = {
    objectType: PropTypes.number.isRequired,
    objectCode: PropTypes.string,
}

export default WaterStressCard